import React, { useState, useEffect, useContext } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import loader from "../../assets/loader.webp";
import SelectLabels from "../../components/SelectLabels";
import { fetchData, createData } from "../../utility/adminApi";
import { notify } from "../../components/toastify";
import useDefaultCurrency from "../../utility/useDefaultCurrency";
import ImageWithPlaceholder from "../../components/ImageWithPlaceholder";
import { formatNumbersWithDecimal } from "../../components/formatNumbersWIthDecimal";
import Pagination from "../../components/Pagination";
import { adminAxios, adminAxiosv3, axiosInstance } from "../../axiosInstance";
import { ProfileDetailsContext } from "../../context/ProfileDetailsProvider";
import UpgradeModal from "./UpgradeModal";

const Invoice = () => {
  const { data: currencyData } = useDefaultCurrency("admin");
  const { profileDetails } = useContext(ProfileDetailsContext);
  const currency = currencyData?.currency?.symbol || "₦";

  const [loading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [discounts, setDiscounts] = useState([]);
  const [selectedDiscount, setSelectedDiscount] = useState("");
  const [taxes, setTaxes] = useState([]);
  const [selectedTax, setSelectedTax] = useState("");
  const [sendOption, setSendOption] = useState("now");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [recurringFrequency, setRecurringFrequency] = useState("weekly");

  // Product pagination and search
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [productSearch, setProductSearch] = useState("");
  const [isPrevLoading, setIsPrevLoading] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);

  // Invoice history states
  const [, setInvoiceHistory] = useState([]);
  const [, setHistoryLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [, setHistoryTotalPage] = useState(1);
  const [historySearch] = useState("");
  const [, setIsHistoryPrevLoading] = useState(false);
  const [, setIsHistoryNextLoading] = useState(false);
  const [, setIsRefreshing] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  // const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch products
  const fetchProducts = async (page = 1, search = "") => {
    try {
      const response = await fetchData("products", page, search);
      setProducts(response?.data?.data || []);
      setTotalPage(response?.data?.last_page || 1);
      setIsLoading(false);
      setIsNextLoading(false);
      setIsPrevLoading(false);
    } catch (error) {
      console.error("Error fetching products:", error);
      setIsLoading(false);
    }
  };

  // Fetch invoice history
  const fetchInvoiceHistory = async (page = 1, search = "") => {
    try {
      setHistoryLoading(true);
      // Build URL with pagination and search
      let url = `invoices?page=${page}`;
      if (search) {
        url += `&searchParam=${search}`;
      }
      
      // Use adminAxios GET request
      const response = await adminAxios.get(url);
      
      // Response structure: { data: [...], current_page, last_page, per_page, total }
      const responseData = response?.data || {};
      setInvoiceHistory(Array.isArray(responseData.data) ? responseData.data : []);
      setHistoryTotalPage(typeof responseData.last_page === 'number' ? responseData.last_page : 1);
      setHistoryLoading(false);
      setIsHistoryNextLoading(false);
      setIsHistoryPrevLoading(false);
      setIsRefreshing(false);
    } catch (error) {
      console.error("Error fetching invoice history:", error);
      setHistoryLoading(false);
      setIsRefreshing(false);
      setInvoiceHistory([]);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchInvoiceHistory(historyPage, historySearch);
  };

  // Check subscription plan (for debugging)
  useEffect(() => {
    console.log(profileDetails);
  }, [profileDetails]);

  // Fetch customers
  const fetchCustomers = async () => {
    try {
      const response = await fetchData("customers", 1, "");
      const customersData = response?.data || [];
      setCustomers(customersData);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  // Fetch discounts
  const fetchDiscounts = async () => {
    try {
      // Get company_id from profileDetails
      const companyId = profileDetails?.user?.company_id;
      
      if (!companyId) {
        console.log("Company ID not found in profileDetails");
        return;
      }

      const response = await axiosInstance.get(`api/discounts/${companyId}`);
      
      // Store discounts in state, keep all active discounts even if not fully configured
      const discountsData = response?.data?.data || [];
      const activeDiscounts = discountsData.filter(
        (discount) => discount.status === "active"
      );
      setDiscounts(activeDiscounts);
    } catch (error) {
      console.error("Error fetching discounts:", error);
      console.error("Error response:", error?.response?.data);
      setDiscounts([]);
    }
  };

  // Fetch taxes (auto-bills)
  const fetchTaxes = async () => {
    try {
      // Get company_id from profileDetails
      const companyId = profileDetails?.user?.company_id;
      
      if (!companyId) {
        console.log("Company ID not found in profileDetails");
        return;
      }

      const response = await adminAxiosv3.post(`auto-bills`, {
        company_id: companyId,
      });
      
      // Store taxes in state, keep all active taxes
      const taxesData = response?.data?.data?.data || [];
      const activeTaxes = taxesData.filter(
        (tax) => tax.is_active === "1" || tax.is_active === 1
      );
      setTaxes(activeTaxes);
    } catch (error) {
      console.error("Error fetching taxes:", error);
      console.error("Error response:", error?.response?.data);
      setTaxes([]);
    }
  };

  useEffect(() => {
    fetchProducts(currentPage, productSearch);
    fetchCustomers();
    fetchInvoiceHistory(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch discounts and taxes when profileDetails is available
  useEffect(() => {
    if (profileDetails?.user?.company_id) {
      fetchDiscounts();
      fetchTaxes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileDetails]);

  useEffect(() => {
    fetchProducts(currentPage, productSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    fetchProducts(1, productSearch);
  }, [productSearch]);

  const handleCurrentPage = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    fetchInvoiceHistory(historyPage, historySearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyPage]);

  useEffect(() => {
    setHistoryPage(1);
    fetchInvoiceHistory(1, historySearch);
  }, [historySearch]);

  // Handle product selection
  const handleAddProduct = (product) => {
    const existingProduct = selectedProducts.find(
      (item) => item.id === product.id
    );

    if (existingProduct) {
      setSelectedProducts(
        selectedProducts.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setSelectedProducts([
        ...selectedProducts,
        {
          ...product,
          quantity: 1,
          unit_price: Number(product.price),
        },
      ]);
    }
  };

  // Handle quantity change
  const handleQuantityChange = (productId, quantity) => {
    const numQuantity = Number(quantity);
    if (numQuantity <= 0) {
      handleRemoveProduct(productId);
      return;
    }
    setSelectedProducts(
      selectedProducts.map((item) =>
        item.id === productId
          ? { ...item, quantity: numQuantity }
          : item
      )
    );
  };

  // Handle remove product
  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter((item) => item.id !== productId));
  };

  // Calculate totals
  const calculateSubtotal = () => {
    return selectedProducts.reduce(
      (sum, item) => sum + Number(item.unit_price) * Number(item.quantity),
      0
    );
  };

  const hasValidDiscountValue = (discount) => {
    if (!discount) return false;
    if (
      discount.discount_value === null ||
      discount.discount_value === undefined ||
      !discount.discount_type
    ) {
      return false;
    }
    return true;
  };

  const calculateDiscountAmount = () => {
    if (!selectedDiscount) return 0;

    const discountObj = discounts.find((d) => d.id === Number(selectedDiscount));
    if (!hasValidDiscountValue(discountObj)) return 0;

    const subtotal = calculateSubtotal();
    const discountValue = Number(discountObj.discount_value);

    // Check if minimum price requirement is met
    if (subtotal < Number(discountObj.minimum_price || 0)) {
      return 0;
    }

    // Apply discount based on type
    if (discountObj.discount_type === "percentage") {
      return (subtotal * discountValue) / 100;
    } else if (discountObj.discount_type === "fixed") {
      return discountValue;
    }

    return 0;
  };
  

  // Get selected discount object
  const getSelectedDiscountObject = () => {
    if (!selectedDiscount) return null;
    return discounts.find((d) => d.id === Number(selectedDiscount));
  };

  // const calculateDiscountAmount = () => {
  //   if (!discount) return 0;
  //   const subtotal = calculateSubtotal();
  //   return (subtotal * Number(discount)) / 100;
  // };

  const calculateTaxAmount = () => {
    if (!selectedTax) return 0;

    const taxObj = taxes.find((t) => t.id === Number(selectedTax));
    if (!taxObj || !taxObj.percentage) return 0;

    const subtotal = calculateSubtotal() - calculateDiscountAmount();
    const taxPercentage = Number(taxObj.percentage);

    return (subtotal * taxPercentage) / 100;
  };

  // Get selected tax object
  const getSelectedTaxObject = () => {
    if (!selectedTax) return null;
    return taxes.find((t) => t.id === Number(selectedTax));
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discountAmount = calculateDiscountAmount();
    const taxAmount = calculateTaxAmount();
    return subtotal - discountAmount + taxAmount;
  };

  // Handle customer selection
  const handleCustomerChange = (customerId) => {
    setSelectedCustomer(customerId);
  };

  // Handle discount selection
  const handleDiscountChange = (discountId) => {
    setSelectedDiscount(discountId);
  };

  // Handle tax selection
  const handleTaxChange = (taxId) => {
    setSelectedTax(taxId);
  };

  const selectedDiscountObj = getSelectedDiscountObject();
  const selectedTaxObj = getSelectedTaxObject();
  const isSelectedDiscountValid = hasValidDiscountValue(selectedDiscountObj);
  const isSelectedDiscountMinMet =
    selectedDiscountObj && selectedDiscountObj.minimum_price
      ? calculateSubtotal() >= Number(selectedDiscountObj.minimum_price)
      : true;

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCustomer) {
      notify("Please select a customer", "error");
      return;
    }

    if (selectedProducts.length === 0) {
      notify("Please select at least one product", "error");
      return;
    }

    if (sendOption === "later" && !scheduledDate) {
      notify("Please select a date for scheduled sending", "error");
      return;
    }

    if (sendOption === "recurring" && !scheduledDate) {
      notify("Please select a start date for recurring invoice", "error");
      return;
    }

    // Check subscription plan before creating invoice
    const subscription = profileDetails?.subscription;
    const plan = subscription?.plan;
    
    console.log("Profile Details:", profileDetails);
    console.log("Subscription:", subscription);
    console.log("Plan:", plan);

    // Check if plan is "pro"
    if (plan?.toLowerCase() !== "pro") {
      // Show upgrade modal
      setIsUpgradeModalOpen(true);
      return;
    }

    setIsLoading(true);

    try {
      const invoiceData = {
        customer_id: selectedCustomer,
        products: selectedProducts.map((item) => ({
          product_id: item.id,
          quantity: item.quantity,
          unit_price: item.unit_price,
        })),
        discount_id: selectedDiscount ? Number(selectedDiscount) : null,
        discount: selectedDiscount ? selectedDiscountObj.discount_value : 0,
        tax_id: selectedTax ? Number(selectedTax) : null,
        tax: selectedTax ? selectedTaxObj.percentage : 0,
        subtotal: calculateSubtotal(),
        discount_amount: calculateDiscountAmount(),
        tax_amount: calculateTaxAmount(),
        total: calculateTotal(),
        send_option: sendOption,
        scheduled_date: sendOption !== "now" ? scheduledDate : null,
        scheduled_time: sendOption !== "now" ? scheduledTime : null,
        recurring_frequency: sendOption === "recurring" ? recurringFrequency : null,
      };

      const response = await createData("invoices/store", invoiceData);

      if (response?.error === false) {
        notify(response?.message || "Invoice created successfully", "success");
        // Reset form
        setSelectedProducts([]);
        setSelectedCustomer("");
        setSelectedDiscount("");
        setSelectedTax("");
        setSendOption("now");
        setScheduledDate("");
        setScheduledTime("");
        // Refresh invoice history
        handleRefresh();
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
      notify(
        error?.response?.data?.message || "Failed to create invoice",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (loading && products.length === 0) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <img src={loader} alt="loading..." className="w-12" />
      </div>
    );
  }

  return (
    <div className="lg:overflow-y-hidden">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-center p-4 w-full border-b-2 pb-2">
        <h3 className="text-xl font-semibold">Create Invoice</h3>
      </div>

      <div className="w-full p-4 flex flex-col lg:flex-row gap-6">
        {/* Left Column - Product Selection */}
        <div className="flex-1 bg-white rounded-lg p-4">
          <h4 className="text-lg font-semibold mb-4">Select Products</h4>
          
          {/* Search */}
          <input
            type="text"
            placeholder="Search products..."
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            className="border-2 border-gray-300 w-full p-2 px-4 rounded-md mb-4 focus:border-teal-500 focus:outline-none"
          />

          {/* Products Table */}
          <div className="w-full overflow-x-auto">
            {products.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No products found
              </div>
            ) : (
              <table className="min-w-full bg-white border border-gray-300">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="py-2 px-4 border-b border-gray-300">Image</th>
                    <th className="py-2 px-4 border-b border-gray-300">Name</th>
                    <th className="py-2 px-4 border-b border-gray-300">Price</th>
                    <th className="py-2 px-4 border-b border-gray-300">Stock</th>
                    <th className="py-2 px-4 border-b border-gray-300">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr
                      key={product.id}
                      className="hover:bg-gray-100 border-b border-gray-300"
                    >
                      <td className="py-2 px-4 flex justify-center">
                        <ImageWithPlaceholder
                          item={product}
                          className="w-10 h-10 rounded-md object-cover"
                          placeholderClassName="w-10 h-10 bg-slate-400 text-white flex justify-center font-medium items-center rounded-md text-xl"
                        />
                      </td>
                      <td className="py-2 px-4 text-center text-wrap">
                        {product?.product_name}
                      </td>
                      <td className="py-2 px-4 text-center">
                        {currency}
                        {formatNumbersWithDecimal(product?.price)}
                      </td>
                      <td className="py-2 px-4 text-center">
                        {product?.available_quantity === null
                          ? "Unlimited"
                          : product?.available_quantity || 0}
                      </td>
                      <td className="py-2 px-4 text-center">
                        <button
                          onClick={() => handleAddProduct(product)}
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                          disabled={
                            product?.available_quantity !== null &&
                            !product?.available_quantity
                          }
                        >
                          <AddIcon className="text-sm" /> Add
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPage > 1 && (
            <Pagination
              onPageChange={handleCurrentPage}
              totalPage={totalPage}
              currentPage={currentPage}
              setIsNextLoading={setIsNextLoading}
              setIsPrevLoading={setIsPrevLoading}
              isPrevLoading={isPrevLoading}
              isNextLoading={isNextLoading}
            />
          )}
        </div>

        {/* Right Column - Invoice Details */}
        <div className="lg:w-96 bg-white rounded-lg p-4">
          <h4 className="text-lg font-semibold mb-4">Invoice Details</h4>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* Customer Selection */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                Customer <span className="text-red-500">*</span>
              </label>
              <SelectLabels
                categoryData={customers}
                text="Select Customer"
                valueKey="id"
                labelKey="name"
                onSelectChange={handleCustomerChange}
                previousValue={selectedCustomer}
              />
            </div>

            {/* Selected Products List */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-2">
                Selected Products
              </label>
              {selectedProducts.length === 0 ? (
                <p className="text-sm text-gray-400 italic">
                  No products selected
                </p>
              ) : (
                <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
                  {selectedProducts.map((item) => (
                    <div
                      key={item.id}
                      className="p-2 border-b border-gray-200 last:border-b-0 flex items-center justify-between gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {item.product_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {currency}
                          {formatNumbersWithDecimal(item.unit_price)} each
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            handleQuantityChange(item.id, e.target.value)
                          }
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                        />
                        <DeleteIcon
                          onClick={() => handleRemoveProduct(item.id)}
                          className="text-red-500 cursor-pointer text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Discount */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                Discount (optional)
              </label>
              {discounts && discounts.length === 0 ? (
                <input
                  type="text"
                  value="No discounts available"
                  disabled
                  className="border-2 border-gray-300 p-2 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              ) : (
                <>
                  <SelectLabels
                    categoryData={discounts}
                    text="Select Discount"
                    required={false}
                    valueKey="id"
                    labelKey="name"
                    onSelectChange={handleDiscountChange}
                    previousValue={selectedDiscount}
                  />
                  {selectedDiscount && selectedDiscountObj && (
                    <>
                      {isSelectedDiscountValid ? (
                        <p className="text-xs text-gray-500 mt-1">
                          {selectedDiscountObj.discount_type === "percentage"
                            ? `${selectedDiscountObj.discount_value}% off`
                            : `${currency}${formatNumbersWithDecimal(
                                selectedDiscountObj.discount_value
                              )} off`}
                          {selectedDiscountObj.minimum_price &&
                            ` (Min: ${currency}${formatNumbersWithDecimal(
                              selectedDiscountObj.minimum_price
                            )})`}
                        </p>
                      ) : (
                        <p className="text-xs text-yellow-600 mt-1">
                          This discount has no amount configured yet. Please
                          update it in Discount Settings before applying.
                        </p>
                      )}
                      {isSelectedDiscountValid && !isSelectedDiscountMinMet && (
                        <p className="text-xs text-orange-600 mt-1">
                          Subtotal must be at least{" "}
                          {currency}
                          {formatNumbersWithDecimal(
                            selectedDiscountObj.minimum_price || 0
                          )}{" "}
                          to apply this discount.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Tax */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                Tax (optional)
              </label>
              {taxes && taxes.length === 0 ? (
                <input
                  type="text"
                  value="No taxes available"
                  disabled
                  className="border-2 border-gray-300 p-2 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              ) : (
                <>
                  <SelectLabels
                    categoryData={taxes}
                    text="Select Tax"
                    required={false}
                    valueKey="id"
                    labelKey="name"
                    onSelectChange={handleTaxChange}
                    previousValue={selectedTax}
                  />
                  {selectedTax && getSelectedTaxObject() && (
                    <p className="text-xs text-gray-500 mt-1">
                      {getSelectedTaxObject().percentage}% tax
                      {getSelectedTaxObject().description && 
                        ` - ${getSelectedTaxObject().description}`}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Send Option */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-2">
                Send Invoice <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sendOption"
                    value="now"
                    checked={sendOption === "now"}
                    onChange={(e) => setSendOption(e.target.value)}
                    className="cursor-pointer"
                  />
                  <span className="text-sm">Send Now</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sendOption"
                    value="later"
                    checked={sendOption === "later"}
                    onChange={(e) => setSendOption(e.target.value)}
                    className="cursor-pointer"
                  />
                  <span className="text-sm">Send Later</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sendOption"
                    value="recurring"
                    checked={sendOption === "recurring"}
                    onChange={(e) => setSendOption(e.target.value)}
                    className="cursor-pointer"
                  />
                  <span className="text-sm">Recurring</span>
                </label>
              </div>

              {/* Scheduled Date/Time for Later */}
              {sendOption === "later" && (
                <div className="mt-3 flex flex-col gap-2">
                  <div className="flex flex-col">
                    <label className="text-xs text-gray-600 mb-1">
                      Scheduled Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
                      required
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs text-gray-600 mb-1">
                      Scheduled Time (Optional)
                    </label>
                    <input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
                    />
                  </div>
                </div>
              )}

              {/* Recurring Options */}
              {sendOption === "recurring" && (
                <div className="mt-3 flex flex-col gap-2">
                  <div className="flex flex-col">
                    <label className="text-xs text-gray-600 mb-1">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
                      required
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs text-gray-600 mb-1">
                      Frequency
                    </label>
                    <select
                      value={recurringFrequency}
                      onChange={(e) => setRecurringFrequency(e.target.value)}
                      className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Invoice Summary */}
            <div className="border-t-2 border-gray-300 pt-4 mt-2">
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">
                    {currency}
                    {formatNumbersWithDecimal(calculateSubtotal())}
                  </span>
                </div>
                {selectedDiscount &&
                  selectedDiscountObj &&
                  isSelectedDiscountValid &&
                  isSelectedDiscountMinMet && (
                    <div className="flex justify-between text-green-600">
                      <span>
                        Discount ({selectedDiscountObj.name}
                        {selectedDiscountObj.discount_type === "percentage"
                          ? ` - ${selectedDiscountObj.discount_value}%`
                          : ` - ${currency}${formatNumbersWithDecimal(
                              selectedDiscountObj.discount_value
                            )}`}
                        ):
                      </span>
                      <span>
                        -{currency}
                        {formatNumbersWithDecimal(calculateDiscountAmount())}
                      </span>
                    </div>
                  )}
                {selectedTax && getSelectedTaxObject() && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Tax ({getSelectedTaxObject().name} - {getSelectedTaxObject().percentage}%):
                    </span>
                    <span className="font-medium">
                      {currency}
                      {formatNumbersWithDecimal(calculateTaxAmount())}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t-2 border-gray-300 pt-2 mt-2">
                  <span>Total:</span>
                  <span>
                    {currency}
                    {formatNumbersWithDecimal(calculateTotal())}
                  </span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || selectedProducts.length === 0 || !selectedCustomer}
              className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold mt-4"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <img src={loader} alt="loading" className="w-5" />
                  <span>Creating Invoice...</span>
                </div>
              ) : (
                `Send Invoice ${sendOption === "now" ? "Now" : sendOption === "later" ? "Later" : "Recurring"}`
              )}
            </button>
          </form>
        </div>
      </div>
      
      {/* Invoice History */}
      {/* <div className="mt-8 bg-white rounded-lg p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
          <div>
            <h4 className="text-lg font-semibold">Invoice History</h4>
            <p className="text-sm text-gray-500">
              Track previously sent invoices and their statuses.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
            <input
              type="text"
              value={historySearch}
              onChange={handleHistorySearchChange}
              placeholder="Search invoices..."
              className="border-2 border-gray-300 w-full lg:w-72 p-2 px-4 rounded-md focus:border-teal-500 focus:outline-none"
            />
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshIcon className={`text-lg ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="whitespace-nowrap">
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </span>
            </button>
          </div>
        </div>

        {historyLoading ? (
          <div className="w-full flex justify-center items-center py-10">
            <img src={loader} alt="loading history" className="w-10" />
          </div>
        ) : invoiceHistory.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            No invoices found.
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr className="bg-gray-100 text-left text-sm font-semibold text-gray-600">
                  <th className="py-2 px-4 border-b">Invoice #</th>
                  <th className="py-2 px-4 border-b">Customer</th>
                  <th className="py-2 px-4 border-b">Amount</th>
                  <th className="py-2 px-4 border-b">Send Option</th>
                  <th className="py-2 px-4 border-b">Status</th>
                  <th className="py-2 px-4 border-b">Created</th>
                </tr>
              </thead>
              <tbody>
                {invoiceHistory && invoiceHistory.map((item) => {
                  const amount =
                    Number(item?.total) ||
                    Number(item?.total_amount) ||
                    Number(item?.subtotal) ||
                    0;
                  const statusClass =
                    (item?.status || "").toLowerCase() === "sent"
                      ? "bg-orange-100 text-orange-700"
                      : (item?.status || "").toLowerCase() === "completed"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-yellow-100 text-yellow-700";
                  return (
                    <tr
                      key={item?.id}
                      className="hover:bg-gray-50 border-b border-gray-200 text-sm"
                    >
                      <td className="py-2 px-4">
                        {item?.invoice_no ||
                          item?.invoice_number ||
                          `INV-${item?.id || "N/A"}`}
                      </td>
                      <td className="py-2 px-4">
                        {item?.customer?.name ||
                          item?.customer_name ||
                          "N/A"}
                      </td>
                      <td className="py-2 px-4 font-semibold">
                        {currency}
                        {formatNumbersWithDecimal(amount)}
                      </td>
                      <td className="py-2 px-4">
                        {formatStatus(item?.send_option)}
                      </td>
                      <td className="py-2 px-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${statusClass}`}
                        >
                          {formatStatus(item?.status || "pending")}
                        </span>
                      </td>
                      <td className="py-2 px-4">
                        {formatDateTime(item?.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {historyTotalPage > 1 && (
          <Pagination
            onPageChange={handleHistoryPageChange}
            totalPage={historyTotalPage}
            currentPage={historyPage}
            setIsNextLoading={setIsHistoryNextLoading}
            setIsPrevLoading={setIsHistoryPrevLoading}
            isPrevLoading={isHistoryPrevLoading}
            isNextLoading={isHistoryNextLoading}
          />
        )}
      </div> */}
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
      />
    </div>
  );
};

export default Invoice;